/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the account, help and inbox popovers.
 * @state-owner overlay host; notifications module owns inbox reads in connected mode
 * @allowed-imports React; shared components and hooks; demo dataset; active context; auth and notifications module public APIs
 * @forbidden changing role without leaving the shell; direct API client
 * @public-api WorkspaceRail
 * @side-effects leave-workspace and sign-out mutations; navigation
 * @decision The account menu offers two distinct exits. 「切換身分」 deletes only the active
 *   context and returns to the role gate with the session alive; 「登出」 ends the session. Making
 *   role changes cost a re-authentication would punish the common case for no security gain.
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useState } from 'react';
import { useActiveContextId, useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { useDeleteActiveContext, useSignOut } from '../../modules/auth';
import { useInbox, useMarkInboxRead } from '../../modules/notifications';
import { useOverlay } from '../../shared/components/OverlayHost';
import { PopoverIcon, type PopoverIconName, SearchIcon } from '../../shared/components/VispoIcon';
import {
  INBOX_STUDENT,
  INBOX_TEACHER,
  NEWS_STUDENT,
  NEWS_TEACHER,
} from '../../shared/lib/demo-data';
import type { WorkspaceSection } from '../../shared/lib/model';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { SettingsDialog } from './SettingsDialog';

const TEACHER_ACCOUNT_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['user', '帳號資料'],
  ['spark', 'AI 助教'],
  ['gear', '設定'],
  ['team', '我的補習班'],
  ['heart', '推薦好友'],
  ['plan', '方案與付款'],
  ['clock', '我的操作紀錄'],
  ['cup', '成就'],
  ['bell', '通知設定'],
];

const STUDENT_ACCOUNT_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['user', '帳號資料'],
  ['gear', '設定'],
  ['heart', '邀請同學'],
  ['cup', '成就與徽章'],
  ['clock', '我的學習紀錄'],
  ['bell', '通知設定'],
];

const HELP_ROWS: readonly (readonly [PopoverIconName, string])[] = [
  ['book', '說明中心'],
  ['spark', '產品更新'],
  ['mega', '功能許願池'],
  ['users', '老師社群'],
  ['badge', '教學認證'],
  ['help', '聯絡客服'],
];

export function AccountPopover() {
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const t = useTranslator();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const { closePopover, openModal } = useOverlay();
  const signOut = useSignOut();
  const leaveWorkspace = useDeleteActiveContext();
  const rows = teacher ? TEACHER_ACCOUNT_ROWS : STUDENT_ACCOUNT_ROWS;

  return (
    <>
      <div className="phd2" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="av lg p">{teacher ? '師' : '柏'}</div>
        <div>
          <b>{teacher ? '王維青' : '王柏睿'}</b>
          <div style={{ fontSize: 14, color: 'var(--outline)' }}>
            {teacher ? '老師 · 試用中' : '學生 · 國二'}
          </div>
        </div>
      </div>
      <div className="sep" />
      {rows.map(([icon, label]) => (
        <button
          className="pi"
          key={label}
          type="button"
          onClick={() => {
            closePopover();
            // Only the settings surface exists so far; the rest still report as unbuilt.
            if (label === '設定') openModal(<SettingsDialog />);
            else if (label === 'AI 助教') openModal(<SettingsDialog initialTab="ai" />);
            else notify(t(label));
          }}
        >
          <PopoverIcon name={icon} />
          {t(label)}
        </button>
      ))}
      <div className="sep" />
      {/*
        Two separate exits, and the order matters: the cheap one first.

        「切換身分」 drops the active context and returns to the role gate with the sign-in intact,
        which is what the no-in-product-role-switch rule actually asks for — leave the workspace to
        change role, rather than change role while working. 「登出」 ends the session itself and is
        the rarer, more expensive action, so it sits last and reads unambiguously.
      */}
      <button
        className="pi"
        type="button"
        onClick={() => {
          closePopover();
          notify(t('已離開工作區，選一個身分再進去'));
          leaveWorkspace.mutate();
        }}
      >
        <PopoverIcon name="swap" />
        {t('切換身分')}
      </button>
      <button
        className="pi"
        type="button"
        onClick={() => {
          closePopover();
          notify(t('已登出'));
          signOut.mutate();
        }}
      >
        <PopoverIcon name="out" />
        {t('登出')}
      </button>
    </>
  );
}

export function HelpPopover() {
  const t = useTranslator();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const { closePopover } = useOverlay();
  return (
    <>
      <div className="phd2">
        <b>{t('需要協助？')}</b>
      </div>
      <label className="srchb">
        <SearchIcon />
        <input placeholder={t('搜尋說明文章')} />
      </label>
      {HELP_ROWS.map(([icon, label]) => (
        <button
          className="pi"
          key={label}
          type="button"
          onClick={() => {
            closePopover();
            notify(t(label));
          }}
        >
          <PopoverIcon name={icon} />
          {t(label)}
        </button>
      ))}
    </>
  );
}

export function InboxPopover({ onNavigate }: { onNavigate(section: WorkspaceSection): void }) {
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const t = useTranslator();
  const { closePopover } = useOverlay();
  const contextId = useActiveContextId();
  const [tab, setTab] = useState<'in' | 'nw'>('in');
  const inboxQuery = useInbox(contextId, true);
  const markRead = useMarkInboxRead(contextId);
  const remote = inboxQuery.data ?? [];
  const inbox = teacher ? INBOX_TEACHER : INBOX_STUDENT;
  const news = teacher ? NEWS_TEACHER : NEWS_STUDENT;

  return (
    <>
      <div className="ptabs">
        <button
          className={tab === 'in' ? 'on' : ''}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setTab('in');
          }}
        >
          {t('收件匣')}
        </button>
        <button
          className={tab === 'nw' ? 'on' : ''}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setTab('nw');
          }}
        >
          {t('有什麼新的')}
        </button>
      </div>
      <div className="inbx">
        {tab === 'in'
          ? inbox.map((row, index) => (
              <button
                className="nrow"
                key={row[1]}
                type="button"
                onClick={() => {
                  const item = remote[index];
                  if (item) markRead.mutate(item);
                  closePopover();
                  onNavigate(teacher ? 'room' : 'act');
                }}
              >
                <span className={`d2 ${row[0] ? '' : 'g'}`} />
                <div className="c3">
                  <b>{row[1]}</b>
                  <p>{row[2]}</p>
                  <em>{row[3]}</em>
                </div>
              </button>
            ))
          : news.map((row) => (
              <div className="nrow" key={row[1]} role="presentation">
                <span className={`d2 ${row[0] ? '' : 'g'}`} />
                <div className="c3">
                  <b>{row[1]}</b>
                  <p>{row[2]}</p>
                  <em>{t('本週')}</em>
                </div>
                <div className="thmb" />
              </div>
            ))}
      </div>
    </>
  );
}
